<!--
  THE PHONE'S TOP — <768px only. ONE row of liquid-glass bubbles: About · Home ·
  Shows · Movies, scrolling sideways. There is no logo here — the owner's words:
  "there's not enough space" — and, since the dock pass, no search circle either:
  Search is a tab now (TabBar.svelte), at thumb height, and a control that exists
  in two places is a control with two answers. The brand keeps its mark on the
  wide shell and the TV; a phone's 390px are for the destinations.

  THE ARTWORK RUNS BEHIND IT, which is the whole point of glass and was missing.
  This row was built from the same tokens as the desktop pills and looked identical
  in a screenshot, and it was still not the redesign: `--chrome-overlap` was zero
  below 768, so the hero started BELOW this block and every bubble was blurring the
  page background. QA proved it without argument — the same phone page over the
  brightest hero in the catalogue and over the darkest gave the identical ground
  under every pill — whatever `--bg` is at the time (it was rgb(7,9,14) when this
  was measured; Rouge moved it to #0b0b0c). `--chrome-float` (app.css) is
  non-zero here and the hero pulls up underneath. Its `--phone-chrome-h` is THIS
  file's height — the row's two vertical paddings plus `--bubble-h` — and the two
  must be changed together or the hero pulls up by the wrong amount.

  IT SCROLLS AWAY WITH THE CONTENT, and that is a decision, not an omission. A
  sticky block is a slice of a 390×844 screen given permanently to navigation
  the user touches once a session, and sticking only part of it puts glass under
  the notch on the first scroll, because CSS cannot add the safe-area inset to an
  element only while it is stuck. The fixed thing on a phone is the tab bar at the
  bottom, where a thumb is. In flow but painted above: it takes a `z-index`,
  because a hero pulled up under a STATIC element would paint its background over
  that element's background.

  NOTHING BETWEEN THE BUBBLES CATCHES A PRESS. The same rule as TopBar.svelte, and
  it only became load-bearing when there was finally something behind it: before,
  274 of 528 probe points landed on this header rather than the page, which cost
  nothing over `--bg` and would cost a hero its top 112px. `pointer-events` is off
  on the block and on the scroller, and back on for the leaves. Horizontal
  scrolling is unaffected — a drag hit-tests to a bubble and the scroll chain walks
  up through ancestors regardless of what they let through.

  Sideways overflow is intended: the bubbles need not fit across 390px and a
  half-visible bubble is the cue that says so. The row snaps.

  THE GLASS IS THREE INGREDIENTS and it is grey plastic without all three: a
  translucent fill, a blur+saturate of whatever is behind it, and a hairline of
  light along the top edge standing in for a lit bevel.

  AND IT HAS TWO REGISTERS, like the wide bar. At the top of the page: exactly
  the row described above. Once the reader scrolls (class `away`, one boolean
  from scrolled.svelte.ts), the bubbles drop to the thin `--nav-*-away` glass —
  the row is already leaving with the content, and this keeps its last visible
  moments from blocking the artwork it is crossing. Same tokens as the wide
  shell, so the two cannot drift. A television never enters the register.
-->
<script lang="ts">
  import Icon from './Icon.svelte';
  import Logo from './Logo.svelte';
  import { browseHref, nav } from './nav.svelte';
  import { scrolled, trackScrolled } from './scrolled.svelte';
  import { topGround } from './top-ground.svelte';

  interface Bubble {
    href: string;
    label: string;
    active: (route: typeof nav.route) => boolean;
  }

  /* The wide header's taxonomy minus Friends (a permanent dock slot at thumb
     height) and minus HOME: the dock has Home too, and the owner cut the
     duplicate - "remove Home from the top, there's already Home at the bottom" -
     to make room for the wordmark on the right. See docs/DESIGN-LANGUAGE.md §4. */
  const BUBBLES: Bubble[] = [
    { href: browseHref('series'), label: 'Shows', active: (r) => r.name === 'browse' && r.kind === 'series' },
    { href: browseHref('movie'), label: 'Movies', active: (r) => r.name === 'browse' && r.kind === 'movie' },
  ];

  /* One rAF-coalesced boolean, one class flip; CSS draws both registers.
     Refuses to run on a TV — see scrolled.svelte.ts. */
  $effect(() => trackScrolled());
</script>

<!-- Chrome, not a group — see the note in `TopBar.svelte`. -->
<header class="top" class:away={scrolled.away} class:pictured={topGround.src !== null} data-dpad-chrome style:--ground-src={topGround.src ? `url('${topGround.src}')` : 'none'}>
  <!-- THE GROUND: the hero picture, huge and blurred, behind the row. See top-ground.svelte.ts. -->
  <div class="ground" aria-hidden="true"></div>
  <div class="row">
    <div class="rail">
      <nav class="bubbles" aria-label="Browse">
        <a class="about" href="/welcome" aria-label="About Jovii" title="About Jovii"><Icon name="info" size={20}/></a>
        {#each BUBBLES as bubble (bubble.href)}
          {@const here = bubble.active(nav.route)}
          <a href={bubble.href} class:here aria-current={here ? 'page' : undefined}>{bubble.label}</a>
        {/each}
      </nav>
    </div>
    <!--
      NO SEARCH CIRCLE HERE ANY MORE. It moved to the dock (TabBar.svelte),
      where the thumb is, and it must not be in two places: one control, one
      home. What sits on this line now: About - Shows - Movies on the left,
      and THE WORDMARK on the right. The owner's first note on this row was
      "there's not enough space" for a logo; with Home and Search both gone to
      the dock there is, and the brand belongs on the page it is a brand for.
      JOVII is a link home, so Home did not really leave the top - it became
      the logo, which is how every streaming app does it.
    -->
    <span class="brand"><Logo href="/" size={22} wordmark={true} /></span>
  </div>
</header>

<style>
  .top {
    /* Not sticky, but positioned: see the note at the top of this file. */
    position: relative;
    z-index: var(--z-bar);
    padding-top: var(--safe-t);
    pointer-events: none;
    /* The one clip that should exist: the blur reaches past the box. */
    overflow: hidden;
  }
  /* THE GROUND (top-ground.svelte.ts). The picture, huge and blurred to
     colour, behind the row and up under the status bar. Full opacity, no
     fade: the row sits ON the artwork and the join into the sharp picture
     below is one surface. */
  .ground {
    position: absolute;
    inset: 0;
    background-image: var(--ground-src);
    background-size: 170% auto;
    background-position: center 35%;
    filter: blur(36px) saturate(1.35) brightness(0.9);
    transform: scale(1.25);
    opacity: 0;
    transition: opacity var(--dur) var(--ease);
    pointer-events: none;
  }
  .top.pictured .ground {
    opacity: 1;
  }
  /* …and back on for the things that are actually drawn. */
  .top :global(a) {
    pointer-events: auto;
  }

  /*
   * ONE LINE. The scroller takes the width the circle does not need; only the
   * right side pads here, because the scroller owns its own left padding so the
   * bubbles can bleed to the screen edge while they scroll.
   *
   * These two vertical paddings + --bubble-h ARE --phone-chrome-h in app.css.
   */
  .row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    /* Bubbles left, wordmark right - the row is balanced by its two ends now,
       not centred as a block. */
    justify-content: space-between;
    padding: var(--space-2) max(var(--page-pad), var(--safe-r)) var(--space-3) max(var(--page-pad), var(--safe-l));
  }
  .brand {
    flex: none;
    display: inline-flex;
    align-items: center;
    /* Same optical row as the bubbles: the wordmark is 22px tall, the pills
       are --bubble-h, so it is vertically centred on the pill line. */
    height: var(--bubble-h);
    padding-right: var(--space-1);
  }

  .rail {
    position: relative;
    flex: 0 1 auto;
    min-width: 0;
  }

  .bubbles {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding-inline: 0;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
    /* The scroller draws nothing; its bubbles do. See the header note. */
    pointer-events: none;
    /*
     * The SNAP padding has to carry the safe-area inset as well, and it did not.
     * Measured with the insets forced to an iPhone 15 Pro's (--safe-l 20px): the
     * row's padding went to 20 correctly, then the proximity snap moved
     * `scrollLeft` to 6 and put the first bubble back at x=14 — six pixels inside
     * the curved corner. Padding and scroll-padding are two owners of one number
     * unless they are written the same way.
     */
    scroll-padding-inline: 0;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .bubbles::-webkit-scrollbar {
    display: none;
  }

  /*
   * THE SAME BUBBLE AS THE TOP BAR'S, from the same tokens. The two shells are
   * one idea seen at two widths and they must not drift: a bubble here and a
   * bubble there were separately-authored copies of "glass pill" and had already
   * diverged in fill, hover, hit size and what "here" looks like.
   *
   * Labels are --text and not --text-dim for the reason written out in
   * TopBar.svelte: a pill floating on a picture cannot afford a secondary colour.
   */
  .bubbles a {
    display: grid;
    place-content: center;
    flex: none;
    height: var(--bubble-h);
    padding-inline: var(--space-4);
    border-radius: 999px;
    scroll-snap-align: start;

    border: 1px solid rgb(255 255 255 / .32);
    background: rgb(50 50 53 / .56);
    backdrop-filter: blur(20px) saturate(150%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / .12);

    color: var(--text);
    text-shadow: var(--nav-ink-shadow);
    font-size: var(--text-sm);
    font-weight: var(--weight-semi);
    letter-spacing: var(--tracking-snug);
    white-space: nowrap;
    transition:
      background-color var(--dur) var(--ease),
      border-color var(--dur) var(--ease),
      color var(--dur) var(--ease),
      box-shadow var(--dur) var(--ease),
      text-shadow var(--dur) var(--ease),
      transform var(--dur-fast) var(--ease);
  }
  .bubbles a.about { width:var(--hit); padding-inline:0; }
  .bubbles a:active {
    transform: scale(0.96);
  }

  .bubbles a.here {
    border-color: var(--nav-line-here);
    background: var(--nav-fill-here);
    box-shadow: var(--glass-shine), var(--nav-glow-here);
  }

  /*
   * THE SCROLLED REGISTER — the wide bar's `.away` treatment, from the same
   * tokens (TopBar.svelte carries the full argument). One extra rule for the
   * thumb: there is no hover here, so it is the PRESS that firms a bubble up —
   * `:active` restores the resting glass under the finger, and `:focus-visible`
   * does the same for a keyboard.
   */
  .top.away .bubbles a {
    background: var(--nav-fill-away);
    border-color: var(--nav-line-away);
    box-shadow: var(--nav-shine-away), var(--nav-shadow-away);
    text-shadow: var(--nav-ink-shadow-away);
  }
  .top.away .bubbles a.here {
    background: var(--nav-fill-here-away);
    border-color: var(--nav-line-here-away);
    box-shadow: var(--nav-shine-away), var(--nav-glow-here-away);
  }
  .top.away .bubbles a:active,
  .top.away .bubbles a:focus-visible {
    background: var(--nav-fill-hover-away);
    border-color: var(--nav-line);
    box-shadow: none;
    text-shadow: var(--nav-ink-shadow);
  }
  .top.away .bubbles a.here:active,
  .top.away .bubbles a.here:focus-visible {
    background: var(--nav-fill-here);
    border-color: var(--nav-line-here);
    box-shadow: var(--glass-shine), var(--nav-glow-here);
  }

  /* The "there is more sideways" cue, and the thing that keeps a half-cut bubble
     from looking like a bug: it ends where the scroller does. Never over the top
     of a hit area — pointer-events off. */

</style>